// src/pages/AdminDashboard.tsx
import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Grid, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import AdminPromotions from './AdminPromotions';
import AdminPanel from './AdminPanel';
import AdminBlog from './AdminBlog';
import UserManagement from './UserManagement';
import { API_BASE_URL } from '../utils/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface Category {
  id: number;
  name: string;
}

interface TransferHubProfile {
  id: number;
  userId: number;
  profession: string;
  rank?: string;
  department?: string;
  currentLocation: string;
  preferredLocations: string[];
  qualifications?: string;
  majors?: string;
  verificationStatus: string;
  createdAt: string;
  User: {
    id: number;
    name: string;
    email: string;
  };
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const { user, token } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [openAnnouncementDialog, setOpenAnnouncementDialog] = useState(false);
  const [openProviderDialog, setOpenProviderDialog] = useState(false);
  const [announcementData, setAnnouncementData] = useState({ title: '', message: '' });
  const [categories, setCategories] = useState<Category[]>([]);
  const [providerData, setProviderData] = useState({
    businessName: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    description: '',
    services: '',
    serviceArea: '',
    operatingHours: '',
    website: '',
    facebook: '',
    instagram: '',
    selectedCategories: [] as number[]
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [transferHubProfiles, setTransferHubProfiles] = useState<TransferHubProfile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategories(data);
      } catch (error) {
        console.error('Failed to load categories', error);
      }
    };

    fetchCategories();
  }, []);

  // Load TransferHub profiles when tab is selected
  useEffect(() => {
    if (tabValue === 7) {
      const fetchProfiles = async () => {
        setLoadingProfiles(true);
        try {
          const response = await fetch(`${API_BASE_URL}/admin/transferhub/profiles`, {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });
          if (!response.ok) throw new Error('Failed to fetch profiles');
          const data = await response.json();
          setTransferHubProfiles(data);
        } catch (error) {
          setMessage({ type: 'error', text: 'Failed to load TransferHub profiles' });
        } finally {
          setLoadingProfiles(false);
        }
      };
      fetchProfiles();
    }
  }, [tabValue, token]);

  const handleCreateAnnouncement = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/announcements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...announcementData,
          createdBy: user?.id
        }),
      });
      
      if (!response.ok) throw new Error('Failed to create announcement');
      
      setMessage({ type: 'success', text: 'Announcement created successfully!' });
      setAnnouncementData({ title: '', message: '' });
      setOpenAnnouncementDialog(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create announcement' });
    }
  };

  const handleCreateProvider = async () => {
    try {
      const servicesArray = providerData.services.split(',').map(s => s.trim());
      if (providerData.selectedCategories.length === 0) {
        setMessage({ type: 'error', text: 'Please select at least one service category.' });
        return;
      }

      const payload = {
        businessName: providerData.businessName,
        contactPerson: providerData.contactPerson,
        phone: providerData.phone,
        email: providerData.email || undefined,
        address: providerData.address,
        description: providerData.description,
        services: servicesArray,
        serviceArea: providerData.serviceArea,
        operatingHours: providerData.operatingHours,
        website: providerData.website || undefined,
        facebook: providerData.facebook || undefined,
        instagram: providerData.instagram || undefined,
        photos: [],
        status: 'pending',
        categories: providerData.selectedCategories
      };

      const response = await fetch(`${API_BASE_URL}/providers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) throw new Error('Failed to create service provider');
      
      setMessage({ type: 'success', text: 'Service provider created successfully!' });
      setProviderData({
        businessName: '',
        contactPerson: '',
        phone: '',
        email: '',
        address: '',
        description: '',
        services: '',
        serviceArea: '',
        operatingHours: '',
        website: '',
        facebook: '',
        instagram: '',
        selectedCategories: []
      });
      setOpenProviderDialog(false);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create service provider' });
    }
  };

  if (user?.role !== 'admin') {
    return (
      <Container>
        <Typography variant="h4" color="error">
          Access Denied. Admin privileges required.
        </Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Admin Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Welcome, {user?.name}! Manage your community content here.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Users" />
          <Tab label="Announcements" />
          <Tab label="Service Providers" />
          <Tab label="Reviews" />
          <Tab label="Promotions" />
          <Tab label="Banner Messages" />
          <Tab label="Blog" />
          <Tab label="TransferHub Profiles" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <UserManagement />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Manage Announcements
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => setOpenAnnouncementDialog(true)}
              sx={{ mt: 2 }}
            >
              Create New Announcement
            </Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Manage Service Providers
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => setOpenProviderDialog(true)}
              sx={{ mt: 2 }}
            >
              Add New Service Provider
            </Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Manage Reviews
            </Typography>
            <Button 
              variant="contained" 
              component={Link} 
              to="/admin/reviews"
              sx={{ mt: 2 }}
            >
              Manage Reviews
            </Button>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <AdminPromotions />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <AdminPanel />
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <AdminBlog />
      </TabPanel>

      <TabPanel value={tabValue} index={7}>
        <TransferHubProfilesPanel 
          profiles={transferHubProfiles}
          loading={loadingProfiles}
          onVerify={async (profileId: number) => {
            try {
              const response = await fetch(`${API_BASE_URL}/admin/transferhub/profiles/${profileId}/verify`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
              });
              if (!response.ok) throw new Error('Failed to verify profile');
              setMessage({ type: 'success', text: 'Profile verified successfully!' });
              // Reload profiles
              const refreshResponse = await fetch(`${API_BASE_URL}/admin/transferhub/profiles`, {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
              });
              if (refreshResponse.ok) {
                const data = await refreshResponse.json();
                setTransferHubProfiles(data);
              }
            } catch (error) {
              setMessage({ type: 'error', text: 'Failed to verify profile' });
            }
          }}
          onUnverify={async (profileId: number) => {
            try {
              const response = await fetch(`${API_BASE_URL}/admin/transferhub/profiles/${profileId}/unverify`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
              });
              if (!response.ok) throw new Error('Failed to unverify profile');
              setMessage({ type: 'success', text: 'Profile unverified successfully!' });
              // Reload profiles
              const refreshResponse = await fetch(`${API_BASE_URL}/admin/transferhub/profiles`, {
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                }
              });
              if (refreshResponse.ok) {
                const data = await refreshResponse.json();
                setTransferHubProfiles(data);
              }
            } catch (error) {
              setMessage({ type: 'error', text: 'Failed to unverify profile' });
            }
          }}
        />
      </TabPanel>

      {/* Announcement Dialog */}
      <Dialog open={openAnnouncementDialog} onClose={() => setOpenAnnouncementDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Announcement</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={announcementData.title}
            onChange={(e) => setAnnouncementData({ ...announcementData, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={announcementData.message}
            onChange={(e) => setAnnouncementData({ ...announcementData, message: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAnnouncementDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateAnnouncement} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Service Provider Dialog */}
      <Dialog open={openProviderDialog} onClose={() => setOpenProviderDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Service Provider</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Business Name"
                fullWidth
                value={providerData.businessName}
                onChange={(e) => setProviderData({ ...providerData, businessName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Contact Person"
                fullWidth
                value={providerData.contactPerson}
                onChange={(e) => setProviderData({ ...providerData, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={providerData.phone}
                onChange={(e) => setProviderData({ ...providerData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                type="email"
                value={providerData.email}
                onChange={(e) => setProviderData({ ...providerData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                fullWidth
                value={providerData.address}
                onChange={(e) => setProviderData({ ...providerData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={providerData.description}
                onChange={(e) => setProviderData({ ...providerData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Services (comma-separated)"
                fullWidth
                placeholder="e.g., Plumbing, Electrical, HVAC"
                value={providerData.services}
                onChange={(e) => setProviderData({ ...providerData, services: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Service Area"
                fullWidth
                value={providerData.serviceArea}
                onChange={(e) => setProviderData({ ...providerData, serviceArea: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Operating Hours"
                fullWidth
                placeholder="e.g., Monday-Friday: 8:00 AM - 6:00 PM"
                value={providerData.operatingHours}
                onChange={(e) => setProviderData({ ...providerData, operatingHours: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Website"
                fullWidth
                value={providerData.website}
                onChange={(e) => setProviderData({ ...providerData, website: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Facebook"
                fullWidth
                value={providerData.facebook}
                onChange={(e) => setProviderData({ ...providerData, facebook: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Instagram"
                fullWidth
                value={providerData.instagram}
                onChange={(e) => setProviderData({ ...providerData, instagram: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="provider-category-label">Service Categories</InputLabel>
                <Select
                  labelId="provider-category-label"
                  multiple
                  value={providerData.selectedCategories}
                  input={<OutlinedInput label="Service Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((value) => {
                        const category = categories.find((cat) => cat.id === value);
                        return <Chip key={value} label={category?.name || value} />;
                      })}
                    </Box>
                  )}
                  onChange={(event) =>
                    setProviderData({
                      ...providerData,
                      selectedCategories: event.target.value as number[]
                    })
                  }
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProviderDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateProvider} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

// TransferHub Profiles Panel Component
interface TransferHubProfilesPanelProps {
  profiles: TransferHubProfile[];
  loading: boolean;
  onVerify: (profileId: number) => Promise<void>;
  onUnverify: (profileId: number) => Promise<void>;
}

const TransferHubProfilesPanel: React.FC<TransferHubProfilesPanelProps> = ({
  profiles,
  loading,
  onVerify,
  onUnverify
}) => {
  const [verifying, setVerifying] = useState<number | null>(null);

  const handleVerify = async (profileId: number) => {
    setVerifying(profileId);
    try {
      await onVerify(profileId);
    } finally {
      setVerifying(null);
    }
  };

  const handleUnverify = async (profileId: number) => {
    setVerifying(profileId);
    try {
      await onUnverify(profileId);
    } finally {
      setVerifying(null);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          TransferHub Profile Verification
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage verification status for TransferHub employee profiles
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : profiles.length === 0 ? (
          <Alert severity="info">No TransferHub profiles found.</Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Profession</strong></TableCell>
                  <TableCell><strong>Current Location</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((profile) => (
                  <TableRow key={profile.id}>
                    <TableCell>{profile.User.name}</TableCell>
                    <TableCell>{profile.User.email}</TableCell>
                    <TableCell>{profile.profession}</TableCell>
                    <TableCell>{profile.currentLocation}</TableCell>
                    <TableCell>
                      <Chip 
                        label={profile.verificationStatus} 
                        color={profile.verificationStatus === 'verified' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {profile.verificationStatus === 'verified' ? (
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          onClick={() => handleUnverify(profile.id)}
                          disabled={verifying === profile.id}
                        >
                          {verifying === profile.id ? 'Processing...' : 'Unverify'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleVerify(profile.id)}
                          disabled={verifying === profile.id}
                        >
                          {verifying === profile.id ? 'Processing...' : 'Verify'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;